import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React from 'react';

import { FormObject } from '../../@types';
import {
  canRenderFormNatively,
  formNeedsServiceContext,
  InteractionForm,
} from './CreateService/sections/InteractionForm';
import { useGetServiceById } from '../../queries/service-query';
import { FormInterceptor } from '../Services/UpdateService/ServiceActionBarFragment/FormInterceptor';

export interface JobFormRowProps {
  serviceId: number;
  form: FormObject;
  forms: FormObject[];
  onChange: (updated: FormObject) => void;
}

/**
 * Renders a single interaction form row.
 *
 * Routing: eligible forms go through the shared new-style `InteractionForm` so
 * the desktop status-change dialog matches Create Service and the mobile sheet.
 * Anything the new renderer cannot fully reproduce — sub-conditions, decision
 * prompts, unsupported field types; see `canRenderFormNatively` — falls back to
 * the legacy `FormInterceptor`, which is left untouched.
 *
 * The routing decision comes *before* any data fetching, deliberately. Most
 * forms this renderer handles need only the service id, so gating them on the
 * fetch would make them wait on a request they never read — and show "Failed to
 * load service" when it failed, despite having everything they needed. In a bulk
 * status change that is also one fetch per such form saved.
 *
 * The exception is a form containing a field that queries against the service
 * itself (FaultSelection, SolutionAction, PartsRequest): those need the whole
 * object, so `formNeedsServiceContext` sends them down the fetching path too.
 */
export const JobFormRow: React.FC<JobFormRowProps> = ({ serviceId, form, forms, onChange }) => {
  if (canRenderFormNatively(form) && !formNeedsServiceContext(form)) {
    return <InteractionForm form={form} serviceId={serviceId} onChange={onChange} />;
  }

  return (
    <ServiceBackedJobFormRow serviceId={serviceId} form={form} forms={forms} onChange={onChange} />
  );
};

/**
 * The branch that needs the service object — either because the form falls back
 * to `FormInterceptor`, or because one of its fields queries against the
 * service. Fetches by serviceId so bulk flows show the right service context per
 * row instead of reusing the first job's service.
 */
const ServiceBackedJobFormRow: React.FC<JobFormRowProps> = ({
  serviceId,
  form,
  forms,
  onChange,
}) => {
  const serviceQuery = useGetServiceById(serviceId);

  if (serviceQuery.isLoading) return <CircularProgress size={20} />;
  if (serviceQuery.isError || !serviceQuery.data) {
    // Fetch failed (or returned nothing). Surface an explicit error with a
    // retry instead of leaving the row as an endless spinner.
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '12px', color: 'error.main' }}>
          Failed to load service #{serviceId}.
        </Typography>
        <Button
          size="small"
          onClick={() => serviceQuery.refetch()}
          sx={{ fontSize: '11px', textTransform: 'none' }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Now that the service is loaded, an eligible form still renders natively —
  // it only came down this path because a field needs the service object.
  if (canRenderFormNatively(form)) {
    return (
      <InteractionForm
        form={form}
        serviceId={serviceId}
        service={serviceQuery.data}
        onChange={onChange}
      />
    );
  }

  return (
    <FormInterceptor
      service={serviceQuery.data}
      value={form}
      onChange={(updated) => {
        if (updated) onChange(updated);
      }}
      onDamagedPartSelect={() => {
        /* noop — not applicable on list page */
      }}
      onAddForm={() => {
        /* noop — not applicable on list page */
      }}
      forms={forms}
    />
  );
};
